import { CallLogsTable } from '@/components/call-logs-table/call-logs-table';
import { useCallLog } from '@/hooks/use-call-log/use-call-log';
import { useExportCallLogs } from '@/hooks/use-export-call-logs/use-export-call-logs';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  DEFAULT_PAGINATION,
  useSortAndPagination,
} from '@/hooks/use-sort-and-pagination/use-sort-and-pagination';
import { CallLogFilters, CallRow } from '@/services/call-log-service/call-log-service.types';
import { Flex, message } from 'antd';
import { Suspense, lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_TIME_RANGE } from '@/store/initial-time-range';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import {
  readUrlBool,
  readUrlCount,
  readUrlNumber,
  readUrlString,
} from '@/utils/url-state/url-state';
import { useUrlSync } from '@/hooks/use-url-sync/use-url-sync';
import { useCompanyAccessGuard } from '@/hooks/use-company-access-guard/use-company-access-guard';
import { useInitialSearchParams } from '@/hooks/use-initial-search-params/use-initial-search-params';
import { useUrlCompanyToStoreSync } from '@/hooks/use-url-company-sync/use-url-company-sync';
import { WithoutRange, useRangedFilter } from '@/hooks/use-time-range-selection/use-ranged-filter';
import { useOnPresetSelected } from '@/hooks/use-time-range-selection/use-on-preset-selected';
import { ExportHistoryDrawer } from '@/components/export-history-drawer/export-history-drawer';
import { useExportHistory } from '@/hooks/use-export-history/use-export-history';
import { useExportHistoryFile } from '@/hooks/use-export-history-file/use-export-history-file';
import layout from '@/styles/layout.module.scss';
import { MAX_ROWS } from '@/services/call-log-service/call-log-service.constants';
import { useCompanyStore, useTimeRangeStore } from '@/store/app.store';
import { useUser } from '@/hooks/use-user/use-user';
import { ConversationsHeader } from '@/components/conversations-header/conversations-header';
import LoadMoreRows from '@/components/load-more-rows/load-more-rows';
import ConversationDrawer from '@/components/conversation-drawer/conversation-drawer';
import dayjs, { Dayjs } from 'dayjs';
import { useCompanySearch } from '@/hooks/use-company-search/use-company-search';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { getApiErrorMessage } from '@/utils/api-error/api-error';
import { useRecordingUrl } from '@/hooks/use-recording-url/use-recording-url';
import { hasPlayableDuration } from '@/utils/helpers';

const FloatingAudioPlayer = lazy(
  () => import('@/components/floating-audio-player/floating-audio-player')
);

// Fallback page size when the URL carries none. Matches the mockup's increment.
const PAGE_SIZE = DEFAULT_PAGINATION.items_per_page;

export function Conversations() {
  const { t } = useTranslation('conversations');
  const [selectedRecord, setSelectedRecord] = useState<
    (CallRow & { recordingUrl?: string }) | undefined
  >(undefined);
  const [isPlaying, setIsPlaying] = useState(false);
  const [pendingRecordId, setPendingRecordId] = useState<number | undefined>(undefined);
  const [isExportHistoryDrawerVisible, setExportHistoryDrawerVisible] = useState(false);
  const { companyName, setCompanyName } = useCompanyStore();
  const { startTime, endTime, setTimeRangeFilter } = useTimeRangeStore();
  const { companies } = useCompaniesList();

  const initialUrl = useInitialSearchParams();

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    startTime && endTime ? [dayjs(startTime), dayjs(endTime)] : null
  );

  const { userData } = useUser();
  const isCompanyAdmin = userData?.roles.find((role) => role.name === 'company_admin');
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hasMultipleCompanies = (companies?.length ?? 0) > 1;

  useUrlCompanyToStoreSync(initialUrl);

  useEffect(() => {
    if (readUrlString(initialUrl, 'company_name')) return;
    if (companyName) {
      setPageFilter((prev) => ({
        ...prev,
        company_name: companyName,
      }));
    } else if (isCompanyAdmin && userData?.default_company) {
      setPageFilter((prev) => ({
        ...prev,
        company_name: userData.default_company,
      }));
    }
  }, [companyName, isCompanyAdmin, userData?.default_company]);

  const {
    callLogExportHistoryData,
    refetch: refetchExportHistory,
    isPending: isLoadingData,
    watchExport,
    exportWatchTimedOut,
    exportWatchFailed,
  } = useExportHistory({
    page: currentPage,
    items_per_page: perPage,
    isActive: isExportHistoryDrawerVisible,
  });
  const [messageApi, contextHolder] = message.useMessage();

  const { callId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [pageFilter, setPageFilter] = useState<WithoutRange<CallLogFilters>>({
    duration_min: readUrlNumber(initialUrl, 'duration_min'),
    duration_max: readUrlNumber(initialUrl, 'duration_max'),
    query: readUrlString(initialUrl, 'query'),
    status: readUrlString(initialUrl, 'status') as CallLogFilters['status'],
    agent: readUrlBool(initialUrl, 'agent'),
    export: undefined,
    company_name:
      readUrlString(initialUrl, 'company_name') ??
      companyName ??
      (isCompanyAdmin ? userData?.default_company : userData?.company?.name),
    topic: readUrlString(initialUrl, 'topic'),
    type: readUrlString(initialUrl, 'type'),
    language: readUrlString(initialUrl, 'language'),
  });

  const { paginationDetails, sortDetails, onPaginationChanged, onSortChange } =
    useSortAndPagination({
      defaultPagination: {
        page: readUrlCount(initialUrl, 'page') ?? DEFAULT_PAGINATION.page,
        items_per_page:
          readUrlCount(initialUrl, 'items_per_page', MAX_ROWS) ?? DEFAULT_PAGINATION.items_per_page,
      },
      defaultSorting: {
        sort_by: readUrlString(initialUrl, 'sort_by') ?? 'created_at',
        order_by: (readUrlString(initialUrl, 'order_by') ?? 'desc') as OrderBy,
      },
    });

  const callLogsFilters = useRangedFilter(pageFilter);

  // No reset: this fires on mount because timeRange defaults, so resetting would discard
  // `page` from every deep link.
  useOnPresetSelected(() => {
    setDates(null);
    setPageFilter((prev) => ({ ...prev, topic: undefined }));
  });

  // This one only fires on a real mismatch, so resetting is safe and correct: the user is
  // being moved to a different company's list.
  useCompanyAccessGuard(callLogsFilters.company_name, companies, (allowedName) => {
    setPageFilter((prev) => ({ ...prev, company_name: allowedName }));
    setCompanyName(allowedName);
    onPaginationChanged(DEFAULT_PAGINATION.page, paginationDetails.items_per_page);
  });

  useUrlSync(
    {
      time_range: callLogsFilters.time_range,
      start_time: callLogsFilters.start_time,
      end_time: callLogsFilters.end_time,
      company_name: callLogsFilters.company_name,
      status: callLogsFilters.status,
      agent: callLogsFilters.agent,
      query: callLogsFilters.query,
      type: callLogsFilters.type,
      language: callLogsFilters.language,
      topic: callLogsFilters.topic,
      duration_min: callLogsFilters.duration_min,
      duration_max: callLogsFilters.duration_max,
      page: paginationDetails.page,
      items_per_page: paginationDetails.items_per_page,
      sort_by: sortDetails?.sort_by,
      order_by: sortDetails?.order_by,
    },
    {
      defaults: {
        time_range: DEFAULT_TIME_RANGE,
        page: DEFAULT_PAGINATION.page,
        items_per_page: DEFAULT_PAGINATION.items_per_page,
        sort_by: 'created_at',
        order_by: 'desc',
      },
    }
  );

  // `page` in the URL means pages loaded, so one request asks for that many pages of rows.
  // Floored again here because the state can also be set from the table, and asking for zero
  // rows renders a one-row table with no error.
  const loadedPages = Math.max(1, Math.floor(paginationDetails.page) || 1);
  const rowsPerPage = Math.max(1, Math.floor(paginationDetails.items_per_page) || PAGE_SIZE);
  // The API clamps at MAX_ROWS silently, so state the ceiling rather than keep offering more.
  const requestedRows = Math.min(rowsPerPage * loadedPages, MAX_ROWS);
  const atCeiling = rowsPerPage * loadedPages >= MAX_ROWS;

  const handleChange = (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => {
    // Sorting arrives with an empty pagination object, so falling back to the constant here
    // would overwrite a page size read from the URL.
    onPaginationChanged(page || loadedPages, perPage || rowsPerPage);
    onSortChange(sortBy || 'created_at', orderBy || 'desc');
  };

  // Models url plus status: a recording still transferring is not a failure.
  const { recordingUrl, recordingStatus, isErrorRecordingUrl } = useRecordingUrl({
    conversationId: pendingRecordId,
    enabled: pendingRecordId !== undefined,
  });

  const handleSelectRecord = (record: CallRow) => {
    // Re-clicking the row that is already loaded is a pause, not a new selection.
    // Replacing selectedRecord would drop recordingUrl, unmount the player and lose the
    // WaveSurfer position, so the next play restarts from 0:00.
    if (selectedRecord?.id === record.id) return;

    setPendingRecordId(record.id);
    setSelectedRecord(record);
  };

  useEffect(() => {
    if (pendingRecordId === undefined) return;

    if (recordingStatus === 'ready' && recordingUrl) {
      setSelectedRecord((prev) => (prev ? { ...prev, recordingUrl } : prev));
      setPendingRecordId(undefined);
      return;
    }

    if (recordingStatus === 'unavailable' || isErrorRecordingUrl) {
      messageApi.open({ type: 'error', content: t('messages.recordingUnavailable') });
      setSelectedRecord(undefined);
      setPendingRecordId(undefined);
      return;
    }

    // 'processing' means the transfer has not finished. Say so once rather than poll forever.
    // Clearing the id here also means the hook's 15s poll never runs a second iteration from
    // this screen. history-panel still relies on it, so the capability is not dead.
    if (recordingStatus === 'processing') {
      messageApi.open({
        type: 'info',
        content: t('messages.recordingProcessing'),
      });
      setSelectedRecord(undefined);
      setPendingRecordId(undefined);
    }
  }, [pendingRecordId, recordingStatus, recordingUrl, isErrorRecordingUrl, messageApi, t]);

  // One player at a time: the drawer has its own, and two WaveSurfer instances on one
  // screen means two clips audible at once.
  useEffect(() => {
    if (!callId) return;
    setSelectedRecord(undefined);
    setPendingRecordId(undefined);
  }, [callId]);

  const {
    callLogData,
    refetch: refetchCallLog,
    trendingTopics,
    isPendingTrendingTopics,
    isPending: isLoadingCallLog,
    isFetching: isFetchingCallLog,
  } = useCallLog({
    filtersParams: {
      ...callLogsFilters,
      company_name: callLogsFilters.company_name,
      time_zone: timeZone,
    },
    // One request for every loaded page, so page is always 1 and the row count carries the
    // accumulation. items_per_page keeps its URL meaning: it is the size of one page.
    paginationParams: { ...sortDetails, page: 1, items_per_page: requestedRows },
    loadedPages,
  });

  const { exportCallLogs, isPending: isExporting } = useExportCallLogs({
    filtersParams: {
      ...callLogsFilters,
      company_name: callLogsFilters.company_name,
    },
    paginationParams: { order_by: 'desc', sort_by: 'created_at', page: 1, items_per_page: 10 },
  });

  const { prepareCSV, isPending, exportContextHolder } = useExportHistoryFile();

  const handleRefreshData = () => {
    refetchCallLog();
  };

  const handlePrepareExportData = async () => {
    try {
      const response = await exportCallLogs();

      if (response) {
        messageApi.open({
          type: 'info',
          content: t('messages.exportProcessing'),
        });
        // watchExport reads page 1 directly, so it does not wait on this flush.
        setCurrentPage(1);
        watchExport();
      }
    } catch (error) {
      // The API refuses a window that is too wide, inverted or unparseable, and its
      // reason is the only thing that tells the user which one. A stale or shared
      // URL is enough to get here, since the date params are read back unvalidated.
      messageApi.open({
        type: 'error',
        content: getApiErrorMessage(error, t('messages.exportFailed')),
      });
    }
  };

  const handleExportData = (id: number) => {
    prepareCSV(id);
  };

  const handleExportHistory = () => {
    // The drawer only toggles a visibility prop, so there is no mount to refetch
    // on. Keeps the current page: page 1 would then be served from cache anyway.
    refetchExportHistory();
    setExportHistoryDrawerVisible(true);
  };

  useEffect(() => {
    if (!exportWatchTimedOut) return;
    const warn = () =>
      messageApi.open({
        type: 'warning',
        content:
          exportWatchTimedOut === 'not-started'
            ? t('messages.exportNotStarted')
            : t('messages.exportStillProcessing'),
      });

    // A toast in a hidden tab self-dismisses unseen, so hold it until the user
    // is looking.
    if (!document.hidden) {
      warn();
      return;
    }
    const showWhenVisible = () => {
      if (document.hidden) return;
      warn();
      document.removeEventListener('visibilitychange', showWhenVisible);
    };
    document.addEventListener('visibilitychange', showWhenVisible);
    return () => document.removeEventListener('visibilitychange', showWhenVisible);
  }, [exportWatchTimedOut, messageApi, t]);

  useEffect(() => {
    if (!exportWatchFailed) return;
    messageApi.open({
      type: 'error',
      content: t('messages.exportStatusCheckFailed'),
    });
  }, [exportWatchFailed, messageApi, t]);

  const closeExportHistoryDrawer = () => {
    setExportHistoryDrawerVisible(false);
  };

  // start_time/end_time are dropped with the rest: the store owns them, so keeping
  // them would put a silently ineffective copy back into page state.
  const handleCompanyChange = ({
    time_range,
    start_time: _start,
    end_time: _end,
    ...filters
  }: CallLogFilters) => {
    setPageFilter(filters);
    setCompanyName(filters.company_name ?? undefined);
    if (time_range) {
      setTimeRangeFilter(time_range);
    }
    onPaginationChanged(DEFAULT_PAGINATION.page, paginationDetails.items_per_page);
  };

  const onClearDateRangeFilter = () => {
    setTimeRangeFilter(DEFAULT_TIME_RANGE);
    setDates(null);
    onPaginationChanged(DEFAULT_PAGINATION.page, paginationDetails.items_per_page);
  };

  const onChangeDateRangeFilter = (filterValue: {
    time_range: { start_time: string; end_time: string };
  }) => {
    setPageFilter((prevState) => ({ ...prevState, topic: undefined }));
    setTimeRangeFilter(
      undefined,
      filterValue.time_range.start_time,
      filterValue.time_range.end_time
    );
    onPaginationChanged(DEFAULT_PAGINATION.page, paginationDetails.items_per_page);
  };

  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const { companySearchData, companySearchLoading, companySearchError } = useCompanySearch({
    query: companySearchQuery,
  });

  return (
    <Flex vertical className={layout.page}>
      <div>
        {contextHolder && <div key="message-holder">{contextHolder}</div>}
        {exportContextHolder && <div key="export-message-holder">{exportContextHolder}</div>}
      </div>

      <ConversationsHeader
        onSearchQueryChange={setCompanySearchQuery}
        searchResults={companySearchData}
        isSearchLoading={companySearchLoading}
        searchError={companySearchError}
        trendingTopics={trendingTopics?.slice(0, 5) || []}
        allTopics={trendingTopics || []}
        onRefreshData={handleRefreshData}
        onExportHistory={handleExportHistory}
        filters={callLogsFilters}
        onChangeFilters={handleCompanyChange}
        companiesList={companies?.map((company) => company.name) || []}
        dates={dates}
        setDates={setDates}
        onDateRangeClear={onClearDateRangeFilter}
        onChangeDateRangeFilter={onChangeDateRangeFilter}
        isTrendingTopicsLoading={isPendingTrendingTopics}
      />

      <ExportHistoryDrawer
        isEmptyData={!(callLogData && callLogData?.calls.length > 0)}
        isExporting={isExporting}
        onPrepareExport={handlePrepareExportData}
        isDownloading={isPending}
        onExportClick={handleExportData}
        isVisible={isExportHistoryDrawerVisible}
        onClose={closeExportHistoryDrawer}
        exportHistoryData={callLogExportHistoryData?.reports}
        isLoadingData={isLoadingData}
        currentPage={currentPage}
        perPage={perPage}
        totalExports={callLogExportHistoryData?.reports_count || 0}
        onChangePagination={(page, pageSize) => {
          setCurrentPage(page);
          setPerPage(pageSize);
        }}
      />

      <CallLogsTable
        onChange={handleChange}
        onSelectRecord={handleSelectRecord}
        callLogData={callLogData?.calls}
        selectedRecord={selectedRecord}
        setIsPlaying={setIsPlaying}
        isPlaying={isPlaying}
        loading={isLoadingCallLog}
        loadingRecordId={recordingStatus === 'ready' ? undefined : pendingRecordId}
        showCompany={hasMultipleCompanies}
      />

      {callLogData && callLogData.calls.length < callLogData.calls_count && (
        <LoadMoreRows
          remaining={callLogData.calls_count - callLogData.calls.length}
          isLowerBound={callLogData.calls_count_relation === 'gte'}
          atCeiling={atCeiling}
          ceiling={MAX_ROWS}
          isLoading={isFetchingCallLog}
          onClick={() => onPaginationChanged(loadedPages + 1, rowsPerPage)}
        />
      )}

      {/* recordingUrl, not just selectedRecord: the row is selected as soon as it is
              clicked so the spinner can show, but the URL arrives a request later and
              mounting the player without a src gives it an empty waveform. */}
      {selectedRecord?.recordingUrl && hasPlayableDuration(selectedRecord.duration) && (
        <Suspense key={selectedRecord.id}>
          <FloatingAudioPlayer
            onPlayerClose={() => setSelectedRecord(undefined)}
            data={selectedRecord}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
          />
        </Suspense>
      )}

      <ConversationDrawer
        callId={callId}
        onClose={() => navigate({ pathname: '/conversations', search: location.search })}
      />
    </Flex>
  );
}
